/**
 * 支付助手 SDK 实例：init / ensure / update / open / rights
 *
 * 【兜底】与充值页同 JS 上下文：事件回调、打开面板、销毁等路径须吞错，
 * 避免助手故障打断选商品/下单。详见 resource_ai_game_buddy/docs/host-isolation-and-error-fallback.md
 */

import { mountVueTip } from '@components/officialTopupNew/payAssistant/agent-tips/mountVueTip';
import {
  loadEntryGuideTip,
  loadEntryWakeTip,
} from '@components/officialTopupNew/payAssistant/agent-tips/loadAgentTip';
import { getAgentChatApi, loadAgentChatScript } from './loadSdk';
import {
  buildPayAssistantChatAppUrl,
  getDefaultChatAppBaseUrl,
} from './buildChatAppUrl';
import {
  createPayAssistantOnEvent,
  trackNeedHelpEntryClick,
  trackCouponUseClick,
  trackPayAssistantHostAction,
  trackPayAssistantSdkError,
} from './analytics';
import { trackCouponApply } from './couponTip';
import {
  applyPayAssistantVisualAdapt,
  resolvePayAssistantMobileBreakpoint,
  startPayAssistantVisualAdapt,
  stopPayAssistantVisualAdapt,
} from './visualAdapt';
import { sdkOpenLink } from '@utiles/officialStore/sdkCtrl';
import { getGlobalGetGotoLink } from '@utiles/globalGetLink';

const PAY_ASSISTANT_CHAT_APP_ID = 'pay-assistant';

/** 与 SDK storage 对齐，浮球点击等未显式传 mode 的路径也走底部抽屉 */
function persistPayAssistantDialogMode() {
  try {
    localStorage.setItem(
      `agent_chat_mode:${PAY_ASSISTANT_CHAT_APP_ID}`,
      'dialog',
    );
  } catch {
    /* localStorage 不可用时忽略 */
  }
}

/** 包装 show：宿主/浮球/tip 等所有入口默认 dialog */
function wrapChatShowWithDialogDefault(chat) {
  if (!chat || typeof chat.show !== 'function') return chat;
  const rawShow = chat.show.bind(chat);
  chat.show = (options, mode) => rawShow(options, mode || 'dialog');
  return chat;
}

const ENTRY_GUIDE_TIP_ID = '__sdk_entry_guide__';
const ENTRY_WAKE_TIP_ID = '__sdk_entry_wake__';

let payAssistantChat = null;
let factory = null;
/** create 同步派发 tip_show 时实例尚未赋值，先挂起 */
let pendingCustomEntryTip = null;

/**
 * @type {{
 *   refreshCouponList?: Function,
 *   getCouponList?: Function,
 *   onBeforeShow?: Function,
 *   onOpenUrl?: Function,
 * } | null}
 */
let hostApi = null;
/** @type {Object|null} */
let payAssistantTheme = null;
/** @type {Object|null} */
let payAssistantEntryTexts = null;
let payAssistantLang = 'en-US';
/** 结果页等场景：不展示「长按暂时不打扰」入口引导气泡 */
let suppressEntryGuideTip = false;

/**
 * 【兜底】init 世代号：destroy 时递增，进行中的 init 在 await 后检测到过期则中止，
 * 避免页面已销毁仍创建浮球/Observer（Home→Result 快速切换）
 */
let initGeneration = 0;

/** init 因 destroy / 页面卸载被中止 */
export class PayAssistantInitAbortedError extends Error {
  constructor() {
    super('PayAssistant init aborted');
    this.name = 'PayAssistantInitAbortedError';
  }
}

/** 未 setup 就取实例（ensure 不再 silent re-init） */
export class PayAssistantNotReadyError extends Error {
  constructor() {
    super('PayAssistant not ready; call setupPayAssistant first');
    this.name = 'PayAssistantNotReadyError';
  }
}

function bumpInitGeneration() {
  initGeneration += 1;
}

function isInitGenerationStale(gen) {
  return gen !== initGeneration;
}

/** 作废 init 中途已 create、尚未 commit 的实例 */
function teardownOrphanChat(chat, sdkFactory) {
  try {
    if (chat && typeof chat.isDestroyed === 'function' && !chat.isDestroyed()) {
      chat.destroy();
    }
    if (sdkFactory) sdkFactory.destroy('pay-assistant');
  } catch (err) {
    console.warn('[PayAssistant] teardown orphan failed', err);
  }
}

export function isPayAssistantInitAborted(err) {
  return !!(err && err.name === 'PayAssistantInitAbortedError');
}

function shouldSuppressEntryGuideTip(payload) {
  return suppressEntryGuideTip && payload && payload.kind === 'guide';
}

/** 结果页等场景：抑制并关闭「长按暂时不打扰」入口引导 */
function applySkipEntryGuideTip(config) {
  if (!config || config.skipEntryGuideTip !== true) return;
  suppressEntryGuideTip = true;
  dismissPayAssistantEntryGuideTip();
}

/** 关闭入口引导气泡（含 SDK 内部 entryTipKind） */
export function dismissPayAssistantEntryGuideTip() {
  const chat = payAssistantChat;
  if (!chat || chat.isDestroyed()) {
    if (pendingCustomEntryTip && pendingCustomEntryTip.kind === 'guide') {
      pendingCustomEntryTip = null;
    }
    return;
  }
  if (typeof chat.dismissEntryGuideTip === 'function') {
    chat.dismissEntryGuideTip();
    return;
  }
  chat.hideTip(ENTRY_GUIDE_TIP_ID);
}

export function getPayAssistantChat() {
  return payAssistantChat;
}

export function getEntryTipStyle() {
  return (payAssistantTheme && payAssistantTheme.entryTipStyle) || {};
}

/** 当前语言的 entryTexts（无则回退 en-US） */
export function getEntryTexts() {
  const texts = payAssistantEntryTexts || {};
  return texts[payAssistantLang] || texts['en-US'] || {};
}

export function getAssistantName() {
  return getEntryTexts().assistantName || '';
}

export function getAgentTipProps() {
  return {
    tipStyle: getEntryTipStyle(),
    assistantName: getAssistantName(),
  };
}

/** 【兜底】登录守卫抛错视为拦截弹窗，不向外抛 */
function invokeHostBeforeShow() {
  const fn = hostApi && hostApi.onBeforeShow;
  if (typeof fn !== 'function') return true;
  try {
    return fn();
  } catch (err) {
    console.error('[PayAssistant] onBeforeShow failed', err);
    trackPayAssistantSdkError(err);
    return false;
  }
}

/**
 * Chat App OPEN_URL：由宿主打开外链（联系客服 / 智能工单等）
 * 【兜底】打开外链失败只打点，不打断 Chat App 事件分发
 */
function invokeHostOpenUrl(url) {
  const fn = hostApi && hostApi.onOpenUrl;
  if (typeof fn !== 'function') return false;
  try {
    fn(url);
    return true;
  } catch (err) {
    console.error('[PayAssistant] onOpenUrl failed', err);
    trackPayAssistantSdkError(err);
    return false;
  }
}

function openUrlViaSdkCtrl(url) {
  if (!url) return;
  sdkOpenLink({ link: getGlobalGetGotoLink({ link: url }) });
}

function mergeHostApi(nextHostApi) {
  if (nextHostApi && typeof nextHostApi === 'object') {
    hostApi = {
      ...(hostApi || {}),
      ...nextHostApi,
    };
  }
}

async function fetchCouponViaHost() {
  const { refreshCouponList, getCouponList } = hostApi || {};
  if (
    typeof refreshCouponList !== 'function' ||
    typeof getCouponList !== 'function'
  ) {
    console.warn('[PayAssistant] hostApi 未注入优惠券方法');
    return null;
  }
  try {
    await refreshCouponList();
  } catch (err) {
    console.error('[PayAssistant] refreshCouponList failed', err);
  }
  const list = getCouponList() || [];
  if (!Array.isArray(list) || !list.length) return null;
  return [...list].sort((a, b) => +a.EXPIRY_TIME - +b.EXPIRY_TIME)[0] || null;
}

/** 【兜底】REQUEST_COUPON 全链路吞错，避免 UnhandledRejection */
async function handleRequestCoupon() {
  try {
    const coupon = await fetchCouponViaHost();
    await withChat(async (chat) => {
      chat.update({
        inputValue: '',
        extensions: {
          skill: 'coupon_detail',
          coupon: coupon || null,
        },
      });
    });
  } catch (err) {
    console.error('[PayAssistant] handleRequestCoupon failed', err);
    trackPayAssistantSdkError(err);
  }
}

async function renderCustomEntryTip(payload, chatOverride) {
  const chat = chatOverride || payAssistantChat;
  if (!chat || chat.isDestroyed()) {
    pendingCustomEntryTip = payload;
    return;
  }
  pendingCustomEntryTip = null;

  const tipId =
    payload.tipId ||
    (payload.kind === 'wake' ? ENTRY_WAKE_TIP_ID : ENTRY_GUIDE_TIP_ID);
  const texts = payload.texts || {};
  const tipStyle = payload.style || getEntryTipStyle();
  const assistantName = texts.assistantName || getAssistantName();

  if (payload.kind === 'wake') {
    const EntryWakeTip = await loadEntryWakeTip();
    // 动态 import 期间可能 destroy；勿对已销毁实例 showTip
    if (!chat || chat.isDestroyed()) return;
    chat.showTip({
      tipId,
      duration: 0,
      bypassHalfMute: true,
      render(el) {
        return mountVueTip(el, EntryWakeTip, {
          tipStyle,
          assistantName,
          wakeMessage: texts.wakeMessage,
          wakeHighlight: texts.wakeHighlight,
        });
      },
    });
    applyPayAssistantVisualAdapt();
    requestAnimationFrame(applyPayAssistantVisualAdapt);
    return;
  }

  const EntryGuideTip = await loadEntryGuideTip();
  if (!chat || chat.isDestroyed()) return;
  chat.showTip({
    tipId,
    duration: 0,
    render(el) {
      return mountVueTip(
        el,
        EntryGuideTip,
        {
          tipStyle,
          assistantName,
          guideMessage: texts.guideMessage,
          guideHighlight: texts.guideHighlight,
          quietBtn: texts.quietBtn,
          gotItBtn: texts.gotItBtn,
        },
        {
          quiet() {
            trackPayAssistantHostAction('tip_quiet_btn', {
              category: 'tip',
              tipKind: 'guide',
              tipId,
            });
            chat.setEntryMode('half');
          },
          gotIt() {
            trackPayAssistantHostAction('tip_got_it_btn', {
              category: 'tip',
              tipKind: 'guide',
              tipId,
            });
            if (typeof chat.dismissEntryGuideTip === 'function') {
              chat.dismissEntryGuideTip();
            } else {
              chat.hideTip(tipId);
            }
          },
        },
      );
    },
  });
  applyPayAssistantVisualAdapt();
  requestAnimationFrame(applyPayAssistantVisualAdapt);
}

function flushPendingCustomEntryTip(chat) {
  if (!pendingCustomEntryTip || !chat) return;
  const payload = pendingCustomEntryTip;
  pendingCustomEntryTip = null;
  if (shouldSuppressEntryGuideTip(payload)) {
    dismissPayAssistantEntryGuideTip();
    return;
  }
  renderCustomEntryTip(payload, chat).catch((err) => {
    console.warn('[PayAssistant] renderCustomEntryTip failed', err);
  });
}

/**
 * SDK / Chat App → 宿主事件
 * 【兜底】同步抛错与异步 fire-and-forget 均不得冒泡到充值页
 */
function handleAgentEvent(type, data) {
  try {
    if (type === 'REQUEST_COUPON') {
      // 【兜底】异步路径自行吞错，避免 UnhandledRejection 打断宿主
      void handleRequestCoupon(data);
      return;
    }

    // 【兜底】Chat App iframe 内运行时错误：仅打点，不影响宿主
    if (type === 'CHAT_APP_RUNTIME_ERROR') {
      trackPayAssistantHostAction('chat_app_runtime_error', {
        category: 'error',
        actor: 'chat_app',
        ...(data && typeof data === 'object' ? data : { data }),
      });
      return;
    }

    // Chat App need-help 快捷入口点击：SDK 不映射 SDK_ANALYTICS，旁路打点
    if (type === 'NEED_HELP_ENTRY_CLICK') {
      trackNeedHelpEntryClick(
        data && typeof data === 'object' ? data : {},
      );
      return;
    }

    // Chat App 优惠券卡「立即使用」：旁路打点，记录券 ID，并关掉对话窗
    if (type === 'COUPON_USE_CLICK') {
      const payload = data && typeof data === 'object' ? data : {};
      trackCouponUseClick(payload);
      trackCouponApply(payload);
      const chat = payAssistantChat;
      if (chat && !chat.isDestroyed() && typeof chat.close === 'function') {
        chat.close();
      }
      return;
    }

    // Chat App 内点击联系客服等外链：window.open 被拦截时通知宿主
    if (type === 'OPEN_URL') {
      const url = data && data.url;
      trackPayAssistantHostAction('open_url', {
        category: 'chat',
        actor: 'chat_app',
        url,
        source: data?.source || '',
      });
      if (!url || !invokeHostOpenUrl(url)) {
        openUrlViaSdkCtrl(url);
      }
      return;
    }

    // 新契约：SDK_ANALYTICS tip_show（props.useSdkEntryTip === false）
    if (type === 'SDK_ANALYTICS') {
      const action = data && data.action;
      const props = (data && data.props) || {};
      if (action === 'tip_show' && props.useSdkEntryTip === false) {
        const payload = {
          kind: props.tipKind,
          tipId: props.tipId,
          texts: props.texts,
          style: props.style,
          useSdkEntryTip: false,
        };
        if (shouldSuppressEntryGuideTip(payload)) {
          dismissPayAssistantEntryGuideTip();
          return;
        }
        // 【兜底】自定义 tip 渲染失败不影响后续事件
        renderCustomEntryTip(payload).catch((err) => {
          console.warn('[PayAssistant] renderCustomEntryTip failed', err);
        });
      }
    }
  } catch (err) {
    // 【兜底】事件处理同步异常：只打点，不抛给 SDK Bridge
    console.error('[PayAssistant] handleAgentEvent failed', type, err);
    trackPayAssistantSdkError(err);
  }
}

/**
 * 只取已有实例，不写配置。业务路径应先 setupPayAssistant。
 * 【兜底】不再无 hostApi 静默 init；未就绪时抛 PayAssistantNotReadyError
 */
export async function ensurePayAssistant() {
  if (payAssistantChat && !payAssistantChat.isDestroyed()) {
    return payAssistantChat;
  }
  throw new PayAssistantNotReadyError();
}

/**
 * 显式热更新：只改传入字段
 * 【兜底】失败返回 false，供 Vue watcher 安全调用
 */
export function updatePayAssistant(options = {}) {
  try {
    const { config, hostApi: nextHostApi } = options;
    mergeHostApi(nextHostApi);

    if (!payAssistantChat || payAssistantChat.isDestroyed()) {
      return false;
    }
    if (!config || typeof config !== 'object') {
      return true;
    }

    const partial = {};
    if (Object.prototype.hasOwnProperty.call(config, 'useBeforeShow')) {
      partial.useBeforeShow = config.useBeforeShow === true;
    }
    if (Object.prototype.hasOwnProperty.call(config, 'lang') && config.lang) {
      payAssistantLang = config.lang;
      partial.lang = config.lang;
    }
    if (config.theme && typeof config.theme === 'object') {
      payAssistantTheme = { ...(payAssistantTheme || {}), ...config.theme };
      partial.theme = payAssistantTheme;
    }
    if (Object.prototype.hasOwnProperty.call(config, 'useFloatDrag')) {
      partial.useFloatDrag = config.useFloatDrag !== false;
    }
    if (
      Object.keys(partial).length &&
      typeof payAssistantChat.updateConfig === 'function'
    ) {
      payAssistantChat.updateConfig(partial);
    }
    return true;
  } catch (err) {
    console.error('[PayAssistant] updatePayAssistant failed', err);
    trackPayAssistantSdkError(err);
    return false;
  }
}

/**
 * 首次创建实例。已存在时只合并 hostApi，不覆盖 SDK 配置。
 * 【兜底】每个 await 后校验 initGeneration，过期则 teardown 并抛 InitAbortedError
 */
export async function initPayAssistant(options = {}) {
  const gen = initGeneration;
  const { config = {}, hostApi: nextHostApi } = options;
  mergeHostApi(nextHostApi);

  if (payAssistantChat && !payAssistantChat.isDestroyed()) {
    applySkipEntryGuideTip(config);
    return payAssistantChat;
  }

  await loadAgentChatScript();
  if (isInitGenerationStale(gen)) {
    throw new PayAssistantInitAbortedError();
  }

  const { loadAgentChatSDK, createLifecycleReporter } = getAgentChatApi();
  payAssistantTheme = config.theme || null;
  payAssistantEntryTexts = config.entryTexts || null;
  payAssistantLang = config.lang || 'en-US';
  suppressEntryGuideTip = config.skipEntryGuideTip === true;
  const chatAppUrl = buildPayAssistantChatAppUrl(
    config.chatAppUrl || getDefaultChatAppBaseUrl(),
    {
      lang: payAssistantLang,
      gameId: config.gameId,
      loginAccount: config.loginAccount,
    },
  );
  const useBeforeShow = config.useBeforeShow === true;
  // 仅 meta 固定 750/1280/1980 时抬高断点；真 PC（device-width）不传，保持默认 768
  const mobileBreakpoint = resolvePayAssistantMobileBreakpoint(config);

  const onEvent = createPayAssistantOnEvent(
    handleAgentEvent,
    createLifecycleReporter,
  );

  const sdk = await loadAgentChatSDK({
    chatAppUrl,
    hideFloatBtn: false,
    lang: payAssistantLang,
    entryTexts: payAssistantEntryTexts || {},
    debug: false,
    lazyMount: true,
    adaptiveResize: true,
    mobilePreferFullscreen: false,
    ...(mobileBreakpoint != null ? { mobileBreakpoint } : {}),
    // 'custom'：SDK_ANALYTICS tip_show 后由宿主 renderCustomEntryTip
    entryTipRender: 'custom',
    useBeforeShow,
    onBeforeShow: invokeHostBeforeShow,
    theme: config.theme || {},
    onEvent,
    // 【兜底】SDK 错误只打日志/打点，不抛回充值页
    onError(err) {
      console.error('[PayAssistant]', err.code, err.message);
      trackPayAssistantSdkError(err);
    },
  });
  if (isInitGenerationStale(gen)) {
    throw new PayAssistantInitAbortedError();
  }

  factory = sdk;
  const chat = sdk.create('pay-assistant', {
    useDrag: config.useDrag !== false,
    useFloatDrag: config.useFloatDrag !== false,
    useHalfFloatDrag: config.useHalfFloatDrag !== false,
    useBeforeShow,
    ...(mobileBreakpoint != null ? { mobileBreakpoint } : {}),
  });
  if (isInitGenerationStale(gen)) {
    teardownOrphanChat(chat, sdk);
    payAssistantChat = null;
    factory = null;
    throw new PayAssistantInitAbortedError();
  }

  persistPayAssistantDialogMode();
  wrapChatShowWithDialogDefault(chat);
  payAssistantChat = chat;
  flushPendingCustomEntryTip(payAssistantChat);
  // 宿主侧视觉适配：仅固定设计稿 viewport 时放大，详见 visualAdapt.js
  startPayAssistantVisualAdapt();
  applySkipEntryGuideTip(config);
  return payAssistantChat;
}

export async function withChat(fn) {
  const chat = await ensurePayAssistant();
  return fn(chat);
}

/**
 * 【兜底】withChat 安全包装：失败只打日志/打点，不向外抛
 * 宿主主流程（按钮、错误弹窗）应优先用本方法或 safeOpenAiPanel
 * @param {(chat: unknown) => unknown} fn
 * @returns {Promise<unknown|null>}
 */
export async function safeWithChat(fn) {
  try {
    return await withChat(fn);
  } catch (err) {
    console.error('[PayAssistant] withChat failed', err);
    trackPayAssistantSdkError(err);
    return null;
  }
}

/**
 * 打开 AI 对话面板（可能 reject，调用方需 catch）
 * 支付主流程请用 {@link safeOpenAiPanel}
 */
export async function openAiPanel(options, mode) {
  options = options || {};
  // 默认 dialog（移动端为底部抽屉），避免沿用 localStorage 的 fullScreen 导致 sheet 拖拽失效
  const displayMode = mode || 'dialog';
  await withChat(async (chat) => {
    await chat.show(options, displayMode);
    // show 后弹层/iframe 才有可测尺寸，连刷两帧保证适配生效
    requestAnimationFrame(() => {
      applyPayAssistantVisualAdapt();
      requestAnimationFrame(applyPayAssistantVisualAdapt);
    });
  });
}

/**
 * 【兜底】打开对话面板（吞错）
 * 供提交错误、失败 tip、按钮点击等宿主主流程调用，避免 UnhandledRejection
 * @returns {Promise<boolean>} 调用链完成且无抛错（不保证弹层已展示，如 onBeforeShow 拦截）
 */
export async function safeOpenAiPanel(options, mode) {
  try {
    await openAiPanel(options, mode);
    return true;
  } catch (err) {
    console.error('[PayAssistant] openAiPanel failed', err);
    trackPayAssistantSdkError(err);
    return false;
  }
}

/**
 * 从权益中心链接解析活动标识
 * 例：https://auto-page.pnsofficial.com/pnsvip01/ → pnsvip01
 */
export function parseRightsActivityId(rightsLink) {
  if (!rightsLink || typeof rightsLink !== 'string') return '';
  const link = rightsLink.trim();
  if (!link) return '';
  try {
    const url = new URL(link);
    const segments = url.pathname.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  } catch (e) {
    const segments = link.replace(/\/+$/, '').split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  }
}

/**
 * 同步权益中心配置给 Chat App
 * 【兜底】失败返回 false，不抛给 mixin watcher
 */
export async function notifyRightsConfig(payload = {}) {
  try {
    const rightsLink = payload.rightsLink || '';
    if (!rightsLink) return false;
    if (!payAssistantChat || payAssistantChat.isDestroyed()) return false;
    if (!(await payAssistantChat.isAvailable())) return false;

    const activityId = parseRightsActivityId(rightsLink);
    payAssistantChat.update({
      inputValue: '',
      extensions: {
        skill: 'rights_config',
        rightsLink,
        activityId,
      },
    });
    return true;
  } catch (err) {
    console.error('[PayAssistant] notifyRightsConfig failed', err);
    trackPayAssistantSdkError(err);
    return false;
  }
}

/**
 * 销毁助手实例（浮层 / Observer / 引用）
 * 【兜底】先递增 initGeneration 作废进行中的 init，再 teardown
 */
export async function destroyPayAssistant() {
  bumpInitGeneration();
  try {
    // 先停适配并还原 zoom/transform，再销毁 SDK 节点
    stopPayAssistantVisualAdapt();
    hostApi = null;
    payAssistantTheme = null;
    payAssistantEntryTexts = null;
    suppressEntryGuideTip = false;
    pendingCustomEntryTip = null;
    if (!payAssistantChat) return;
    payAssistantChat.destroy();
    if (factory) factory.destroy('pay-assistant');
  } catch (err) {
    console.error('[PayAssistant] destroyPayAssistant failed', err);
  } finally {
    // 【兜底】无论 destroy 是否成功，都释放单例引用
    payAssistantChat = null;
    factory = null;
  }
}
